import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import DashboardLayout from 'layouts/pages/components/BaseLayout';
import Footer from 'prefabsComponents/Footer';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

// Importamos los componentes de cada paso
import General from 'layouts/pages/orders/order-add/components/OrderGeneral';
import Embarques from 'layouts/pages/orders/order-add/components/OrderEmbarques';

function getSteps() {
  return ['General', 'Embarques'];
}

const isEmpty = (data) => {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object') return Object.keys(data).length === 0;
  return false;
};

function getStepContent(stepIndex, formData, handleFormDataChange, missingFields) {
  switch (stepIndex) {
    case 0:
      return (
        <General
          initialData={formData.general}
          handleFormDataChange={handleFormDataChange}
          missingFields={missingFields}
        />
      );
    case 1:
      return (
        <Embarques
          formData={formData.embarques}
          handleFormDataChange={handleFormDataChange}
        />
      );
    default:
      return null;
  }
}

function Wizard() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  // Estado para controlar qué campos obligatorios faltan (para resaltarlos en rojo)
  const [missingFields, setMissingFields] = useState([]);

  const steps = getSteps();
  const isLastStep = activeStep === steps.length - 1;

  useEffect(() => {
    const savedFormData = JSON.parse(localStorage.getItem('formData'));
    const savedTableData = JSON.parse(localStorage.getItem('embarqueTableData'));
    const savedFiles = JSON.parse(localStorage.getItem('uploadedFiles'));

    const hasValidDraftData =
      !isEmpty(savedFormData) ||
      !isEmpty(savedTableData) ||
      !isEmpty(savedFiles);

    if (hasValidDraftData) {
      Swal.fire({
        title: 'Hay datos en borrador',
        text: '¿Desea continuar con el borrador o descartarlo?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continuar con el borrador',
        cancelButtonText: 'Descartar',
        cancelButtonColor: '#f52727',
        allowOutsideClick: false, // Evita que se cierre haciendo clic fuera del modal
        allowEscapeKey: false, // Evita que se cierre presionando ESC
      }).then((result) => {
        if (result.isConfirmed) {
          setFormData({
            general: savedFormData || {},
            embarques: savedTableData || [],
          });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          localStorage.removeItem('formData');
          localStorage.removeItem('embarqueTableData');
          localStorage.removeItem('uploadedFiles');
          setFormData({
            general: {},
            embarques: [],
          });
        }
        setIsDraftLoaded(true);
      });
    } else {
      setFormData({
        general: {},
        embarques: [],
      });
      setIsDraftLoaded(true);
    }
  }, []);

  // Cada vez que se actualicen los datos del formulario se guardan en localStorage
  const handleFormDataChange = (newData) => {
    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, ...newData };

      // Guardamos cada sección por separado en el localStorage
      if (isDraftLoaded) {
        localStorage.setItem(
          'formData',
          JSON.stringify(updatedFormData.general || {})
        );
        localStorage.setItem(
          'embarqueTableData',
          JSON.stringify(updatedFormData.embarques || [])
        );
      }
      return updatedFormData;
    });
  };

  // Limpieza del localStorage si alguna sección queda vacía
  useEffect(() => {
    if (isDraftLoaded) {
      if (isEmpty(formData.general)) {
        localStorage.removeItem('formData');
      }
      if (isEmpty(formData.embarques)) {
        localStorage.removeItem('embarqueTableData');
      }
    }
  }, [formData, isDraftLoaded]);

  const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);

  // Validamos que los campos obligatorios de la sección "General" estén llenos
  const validateAllFields = () => {
    const requiredFields = [
      'referencia',
      'numeroOrden',
      'origen',
      'fechaEntrega',
      'transporte',
      'cliente',
      'proveedor',
      'fechaRealEntrega',
      'incoterms',
    ];

    const generalData = formData.general || {};
    const emptyFields = [];

    requiredFields.forEach((field) => {
      if (!generalData[field] || generalData[field] === '') {
        emptyFields.push(field);
      }
    });

    if (emptyFields.length > 0) {
      setMissingFields(emptyFields);
      Swal.fire({
        title: 'Faltan campos obligatorios',
        text: 'Por favor, completa todos los campos requeridos.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return false;
    }

    setMissingFields([]);
    return true;
  };

  const saveOrder = async () => {
    if (!validateAllFields()) return;

    try {
      const token = Cookies.get('access_token');
      let user_id = null;
      if (token) {
        try { user_id = jwtDecode(token)?.user_id; } catch { }
      }

      const general = JSON.parse(localStorage.getItem('formData')) || {};
      const embarquesData = JSON.parse(localStorage.getItem('embarqueTableData')) || {};

      const payload = { user_id, general, embarques: embarquesData.rows || [] };

      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/`,
        payload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true, // si además quieres que viaje la cookie
        }
      );

      Swal.fire({ title: 'Orden guardada', text: 'La orden se ha guardado correctamente.', icon: 'success' })
        .then(() => navigate('/ordenes'));

      localStorage.removeItem('formData');
      localStorage.removeItem('embarqueTableData');
      setFormData({ general: {}, embarques: [] });
      setActiveStep(0);
    } catch (error) {
      console.error('Error al guardar la orden:', error);
      const msg = error.response?.data?.error || error.message;
      Swal.fire({ title: 'Error', text: msg, icon: 'error' });
    }
  };

  if (!isDraftLoaded || formData === null) {
    return null;
  }

  return (
    <DashboardLayout>
      <SoftBox pt={3} pb={8}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <SoftBox mt={6} mb={1} textAlign="center">
              <SoftBox mb={1}>
                <SoftTypography variant="h3" fontWeight="bold">
                  Agregar Orden de Compra
                </SoftTypography>
              </SoftBox>
            </SoftBox>

            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <SoftBox display="flex" justifyContent="flex-start">
              <Card sx={{ width: '150vh', height: 'auto', ml: 0 }}>
                <SoftBox p={2}>
                  {getStepContent(activeStep, formData, handleFormDataChange, missingFields)}
                  <SoftBox
                    mt={3}
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {activeStep === 0 ? (
                      <SoftBox />
                    ) : (
                      <SoftButton variant="gradient" color="light" onClick={handleBack}>
                        Volver
                      </SoftButton>
                    )}

                    {isLastStep ? (
                      <SoftButton variant="gradient" color="success" onClick={saveOrder}>
                        Guardar Orden
                      </SoftButton>
                    ) : (
                      <SoftBox display="flex" alignItems="center">
                        <SoftButton variant="gradient" color="success" onClick={saveOrder}>
                          Guardar Orden
                        </SoftButton>
                        <SoftButton
                          variant="gradient"
                          color="dark"
                          onClick={handleNext}
                          ml={1}
                          style={{ marginLeft: '8px' }}
                        >
                          Agregar Embarques
                        </SoftButton>
                      </SoftBox>
                    )}
                  </SoftBox>
                </SoftBox>
              </Card>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Wizard;
